import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getStoredSession, type SessionState } from '../lib/auth-client';

interface SessionContextValue {
  session: SessionState | null;
  setSession: (s: SessionState | null) => void;
  /** Regra 2 (2026-07-28) — a sessão expirou numa ação, mas a página SEGUE MONTADA.
   *  Quem consome deve: exibir o aviso NO LUGAR e DESABILITAR as ações que mutam
   *  (senão vira limbo: botões vivos que só devolvem 401). A saída é sempre manual
   *  e explícita — "Entrar novamente" —, nunca navegação automática. */
  needsReauth: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSessionState] = useState<SessionState | null>(() => getStoredSession());
  const [needsReauth, setNeedsReauth] = useState(false);

  // Entrar (login/first-access) ou sair (logout) encerra a pendência de
  // reautenticação — senão o aviso sobreviveria ao próprio conserto.
  const setSession = useCallback((s: SessionState | null): void => {
    setSessionState(s);
    setNeedsReauth(false);
  }, []);

  // Listen to storage events for cross-tab logout.
  useEffect(() => {
    const onStorage = (): void => {
      setSessionState(getStoredSession());
    };
    window.addEventListener('storage', onStorage);

    // BUG 3 + regra 2 (2026-07-28) — 401 na mesma aba (apiFetch dispara este
    // evento; `storage` só cruza abas).
    //
    // ANTES: re-lia a sessão → null → App.tsx:93 forçava a rota de login → a
    // página DESMONTAVA e o texto em curso morria. Com TTL de inatividade de 30
    // min e digitação que NÃO gera request, isso alcança justamente quem redigiu
    // uma fundamentação longa — no clique do "emitir". A perda era NOSSA: o 401
    // só disse "reautentique", não "descarte o trabalho".
    //
    // AGORA: marca `needsReauth` e NÃO toca em `session` — a árvore React segue
    // montada, o texto continua no campo, e a página mostra o aviso no lugar.
    // O QUE MUDOU É SÓ A CONSEQUÊNCIA: *quando* a sessão é inválida continua
    // idêntico (o apiFetch já zerou o token; o servidor é a autoridade). Nenhum
    // byte a mais em repouso — a preservação é a árvore viva, não storage.
    const onExpired = (): void => {
      setNeedsReauth(true);
    };
    window.addEventListener('comite:session-expired', onExpired);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('comite:session-expired', onExpired);
    };
  }, []);

  return (
    <SessionContext.Provider value={{ session, setSession, needsReauth }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
